/**
 * InferenceReviewCards — READ-ONLY advisory cards.
 *
 * Renders one card per inference kind plus a per-technician card.
 * Confidence is surfaced as a clearly-marked tag; <0.4 confidence
 * is flagged "informational only" so the user knows the wizard
 * will not auto-prefill it.
 *
 * IRON-CLAD RULES:
 *   - These cards NEVER write anything. No buttons, no inputs.
 *     User decisions happen on the LeanSetupWizard page (next
 *     step), where the user can change every field before
 *     committing.
 *   - Missing-or-low-confidence inferences are shown with a
 *     visible "Low confidence" tag — we don't hide them, but we
 *     don't pretend they're authoritative either.
 */

import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import type {
  SetupInferenceRow,
  TechnicianInferredProfileRow,
} from '@/lib/onboarding/inferenceLoader';
import { _internal } from '@/lib/onboarding/inferenceLoader';

const { CONFIDENCE_THRESHOLD, DAY_KEYS } = _internal;

interface ShopHoursPayload {
  byWeekday: Array<{
    weekday: number;
    openHHMM: string | null;
    closeHHMM: string | null;
    sampleSize: number;
  }>;
  /**
   * Batch 8: optional source field. When absent (legacy rows), the
   * card falls back to the CSV-inference label. Future Google Places
   * / Foursquare wiring sets this to 'public_listing'.
   */
  source?: 'csv_inference' | 'public_listing' | 'default' | 'manual';
}

interface ServiceMixPayload {
  buckets: Array<{ bucketKey: string; label: string; jobCount: number; fraction: number }>;
  totalJobs: number;
}

interface VehicleMixPayload {
  topMakes: Array<{ make: string; count: number; fraction: number }>;
  classBreakdown: Array<{ classKey: string; label: string; count: number; fraction: number }>;
  totalVehicles: number;
}

interface ParkingPayload {
  suggestedSpotCount: number;
  observedPeak: number;
  observedAverage: number;
}

interface RosterPayload {
  technicians: Array<{
    externalTechnicianId: string;
    displayName: string;
    jobCount: number;
    appearsFullTime: boolean;
    firstSeenAt?: string;
    lastSeenAt?: string;
  }>;
}

function fmtPct(fraction: number): string {
  return `${Math.round(fraction * 100)}%`;
}

function ConfidenceTag({ confidence }: { confidence: number }) {
  const low = confidence < CONFIDENCE_THRESHOLD;
  return (
    <Badge
      variant={low ? 'outline' : 'secondary'}
      data-testid="inference-confidence"
      data-low={low}
    >
      Confidence {Math.round(confidence * 100)}%{low ? ' — informational only' : ''}
    </Badge>
  );
}

function findInference(
  inferences: SetupInferenceRow[],
  kind: SetupInferenceRow['inference_kind'],
): SetupInferenceRow | undefined {
  return inferences.find((i) => i.inference_kind === kind);
}

export interface InferenceReviewCardsProps {
  inferences: SetupInferenceRow[];
  technicianProfiles: TechnicianInferredProfileRow[];
}

export function InferenceReviewCards({
  inferences,
  technicianProfiles,
}: InferenceReviewCardsProps) {
  return (
    <div className="space-y-4" data-testid="inference-review-cards">
      <ShopHoursCard inference={findInference(inferences, 'shop_hours')} />
      <TechnicianRosterCard
        inference={findInference(inferences, 'technician_roster')}
        profiles={technicianProfiles}
      />
      <ServiceMixCard inference={findInference(inferences, 'service_mix')} />
      <VehicleMixCard inference={findInference(inferences, 'vehicle_mix')} />
      <ParkingCard inference={findInference(inferences, 'parking_heuristic')} />
    </div>
  );
}

function ShopHoursCard({ inference }: { inference: SetupInferenceRow | undefined }) {
  if (!inference) {
    return <EmptyCard title="Shop hours" testid="inference-shop-hours-empty" />;
  }
  const payload = inference.payload as unknown as ShopHoursPayload;
  // Batch 8: surface where these hours came from so the operator can
  // judge how much to trust them. We prefer public-listing data when
  // present; CSV inference is a fallback.
  const source = payload.source ?? 'csv_inference';
  const sourceLabel =
    source === 'public_listing'
      ? 'Found from public business listing'
      : source === 'manual'
        ? 'Manually entered'
        : source === 'default'
          ? 'Default suggestion — please review'
          : 'Inferred from repair-order timestamps';
  return (
    <Card data-testid="inference-shop-hours" data-source={source}>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <div>
          <CardTitle className="text-base">Shop hours</CardTitle>
          <CardDescription data-testid="inference-shop-hours-source">
            {sourceLabel}.
          </CardDescription>
        </div>
        <ConfidenceTag confidence={inference.confidence} />
      </CardHeader>
      <CardContent className="space-y-1 text-sm">
        {payload.byWeekday.map((d) => {
          const dayLabel = DAY_KEYS[d.weekday] ?? `day-${d.weekday}`;
          return (
            <div
              key={d.weekday}
              className="flex justify-between"
              data-testid={`inference-shop-hours-row-${d.weekday}`}
            >
              <span className="capitalize">{dayLabel}</span>
              <span className="text-muted-foreground">
                {d.sampleSize === 0
                  ? '—'
                  : `${d.openHHMM ?? '?'} – ${d.closeHHMM ?? '?'} · ${d.sampleSize} RO${
                      d.sampleSize === 1 ? '' : 's'
                    }`}
              </span>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}

function TechnicianRosterCard({
  inference,
  profiles,
}: {
  inference: SetupInferenceRow | undefined;
  profiles: TechnicianInferredProfileRow[];
}) {
  if (!inference) {
    return <EmptyCard title="Technicians" testid="inference-tech-roster-empty" />;
  }
  const payload = inference.payload as unknown as RosterPayload;
  const profileByName = new Map(
    profiles.map((p) => [p.external_technician_id, p] as const),
  );
  return (
    <Card data-testid="inference-tech-roster">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <div>
          <CardTitle className="text-base">Technicians</CardTitle>
          <CardDescription>
            People who appeared as the assigned technician on historical jobs.
          </CardDescription>
        </div>
        <ConfidenceTag confidence={inference.confidence} />
      </CardHeader>
      <CardContent className="space-y-2 text-sm">
        {payload.technicians.length === 0 && (
          <div className="text-muted-foreground" data-testid="inference-tech-empty">
            No technicians detected.
          </div>
        )}
        {payload.technicians.map((t) => {
          const profile = profileByName.get(t.externalTechnicianId);
          return (
            <div
              key={t.externalTechnicianId}
              className="rounded border bg-muted/30 p-2"
              data-testid={`inference-tech-row-${t.externalTechnicianId}`}
            >
              <div className="flex items-center justify-between">
                <div className="font-medium">{t.displayName}</div>
                <Badge
                  variant={t.appearsFullTime ? 'default' : 'outline'}
                  data-testid={`inference-tech-fulltime-${t.externalTechnicianId}`}
                  data-fulltime={t.appearsFullTime}
                >
                  {t.appearsFullTime ? 'Full-time' : 'Part-time'}
                </Badge>
              </div>
              <div className="text-xs text-muted-foreground">
                {t.jobCount} job{t.jobCount === 1 ? '' : 's'}
                {profile && (
                  <>
                    {' · '}
                    {profile.median_labor_hours !== null
                      ? `${profile.median_labor_hours.toFixed(2)}h median`
                      : 'no hours data'}
                    {' · '}
                    Comeback {fmtPct(profile.comeback_rate)} · Warranty{' '}
                    {fmtPct(profile.warranty_rate)}
                  </>
                )}
              </div>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}

function ServiceMixCard({ inference }: { inference: SetupInferenceRow | undefined }) {
  if (!inference) {
    return <EmptyCard title="Service mix" testid="inference-service-mix-empty" />;
  }
  const payload = inference.payload as unknown as ServiceMixPayload;
  return (
    <Card data-testid="inference-service-mix">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <div>
          <CardTitle className="text-base">Service mix</CardTitle>
          <CardDescription>
            What kinds of jobs your historical work was made of.
          </CardDescription>
        </div>
        <ConfidenceTag confidence={inference.confidence} />
      </CardHeader>
      <CardContent className="space-y-1 text-sm">
        {payload.buckets.length === 0 && (
          <div className="text-muted-foreground">No buckets matched.</div>
        )}
        {payload.buckets.map((b) => (
          <div key={b.bucketKey} className="flex justify-between">
            <span>{b.label}</span>
            <span
              className="text-muted-foreground"
              data-testid={`inference-bucket-${b.bucketKey}`}
            >
              {b.jobCount} job{b.jobCount === 1 ? '' : 's'} · {fmtPct(b.fraction)}
            </span>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

function VehicleMixCard({ inference }: { inference: SetupInferenceRow | undefined }) {
  if (!inference) {
    return <EmptyCard title="Vehicle mix" testid="inference-vehicle-mix-empty" />;
  }
  const payload = inference.payload as unknown as VehicleMixPayload;
  return (
    <Card data-testid="inference-vehicle-mix">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <div>
          <CardTitle className="text-base">Vehicle mix</CardTitle>
          <CardDescription>What you typically work on.</CardDescription>
        </div>
        <ConfidenceTag confidence={inference.confidence} />
      </CardHeader>
      <CardContent className="grid grid-cols-1 gap-2 text-sm md:grid-cols-2">
        <div>
          <div className="text-xs font-medium uppercase text-muted-foreground">Top makes</div>
          {payload.topMakes.slice(0, 5).map((m) => (
            <div key={m.make} className="flex justify-between" data-testid={`inference-make-${m.make}`}>
              <span>{m.make}</span>
              <span className="text-muted-foreground">
                {m.count} · {fmtPct(m.fraction)}
              </span>
            </div>
          ))}
        </div>
        <div>
          <div className="text-xs font-medium uppercase text-muted-foreground">Class</div>
          {payload.classBreakdown.map((c) => (
            <div
              key={c.classKey}
              className="flex justify-between"
              data-testid={`inference-class-${c.classKey}`}
            >
              <span>{c.label}</span>
              <span className="text-muted-foreground">{fmtPct(c.fraction)}</span>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function ParkingCard({ inference }: { inference: SetupInferenceRow | undefined }) {
  if (!inference) {
    return <EmptyCard title="Operational parking demand" testid="inference-parking-empty" />;
  }
  const payload = inference.payload as unknown as ParkingPayload;
  // Batch 8 copy: this number is volume-based (busiest-day RO peak),
  // NOT a physical lot measurement. Sat/parcel-based capacity
  // estimation lives in a future batch (location/lat-lng captured
  // now is the foundation — see `shop_profile.shop_lat/shop_lng`).
  return (
    <Card data-testid="inference-parking">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <div>
          <CardTitle className="text-base">Operational parking demand</CardTitle>
          <CardDescription>
            Based on the busiest historical day's RO volume — not a physical
            lot measurement. Treat as an operational suggestion; we'll improve
            this with satellite / parcel data once your address is on file.
          </CardDescription>
        </div>
        <ConfidenceTag confidence={inference.confidence} />
      </CardHeader>
      <CardContent className="grid grid-cols-3 gap-2 text-sm">
        <Stat label="Volume-based" value={payload.suggestedSpotCount} testid="inference-parking-suggested" />
        <Stat label="Peak day ROs" value={payload.observedPeak} testid="inference-parking-peak" />
        <Stat label="Avg day ROs" value={payload.observedAverage} testid="inference-parking-avg" />
      </CardContent>
    </Card>
  );
}

function EmptyCard({ title, testid }: { title: string; testid: string }) {
  return (
    <Card data-testid={testid}>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">{title}</CardTitle>
        <CardDescription>No inference produced — fill this in manually.</CardDescription>
      </CardHeader>
    </Card>
  );
}

function Stat({
  label,
  value,
  testid,
}: {
  label: string;
  value: number;
  testid: string;
}) {
  return (
    <div className="rounded border bg-muted/40 px-2 py-1">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="text-base font-semibold" data-testid={testid}>
        {value}
      </div>
    </div>
  );
}
